import React, { useEffect, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

import BaseLayout from '../../components/layout/BaseLayout';
import { DataState } from '../../context/DataProvider';
import AutoResizableTextarea from '../../components/AutoResizableTextarea';
import SelectedTriggerItem from '../../components/form/SelectedTriggerItem';
import { createTempBot } from '../../services/api';
import Button from '../../components/custom/Button';
import TriggerModal from '../../components/form/TriggerModal';
import { dataSources, integrationData } from '../../utils/constants';
import QuestionForm from '../../components/form/QuestionForm';
import Modal from '../../components/Modal';
import KanbanBoard from '../../components/kanbanboard/KanbanBoard';
import Backlog from '../../components/backlog/Backlog';

const CreateMeetingForm = ({ isModalOpen, closeModal }) => {
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const [isDataSourceModalOpen, setIsDataSourceModalOpen] = useState(false);

  const [selectTrigerOptions, setSelectTrigerOptions] = useState([]);
  const [selectIntegrationOptions, setSelectIntegrationOptions] = useState([]);
  const [selectDataSourceOptions, setSelectDataSourceOptions] = useState([]);
  const {
    createAndHandleClarificationQuestions,
    setError,
    botCreationId,
    setBotCreationId,
    userGoogleId,
  } = DataState();
  const [editOption, setEditOption] = useState(null);
  const [triggerName, setTriggerName] = useState(''); // State for storing the trigger name
  const [instruction, setInstruction] = useState(''); // State for storing the instruction
  const [meetingName, setMeetingName] = useState(''); // State for storing the instruction
  const [meetingLink, setMeetingLink] = useState(''); // State for storing the instruction
  const [meetingTiming, setMeetingTiming] = useState(''); // State for storing the instruction

  useEffect(() => {
    if (isModalOpen) {
      const createBot = async () => {
        try {
          const response = await createTempBot({}, userGoogleId);
          if (response.error) {
            setError(JSON.stringify(response.error));
            return;
          }
          if (response.bot_id) {
            setBotCreationId(response.bot_id);
          }
        } catch (err) {
          setError(JSON.stringify(err));
        }
      };
      createBot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const handleDeleteIntegrationOption = (optionId) => {
    setSelectIntegrationOptions((prev) =>
      prev.filter((item) => item.id !== optionId),
    );
  };
  const handleDeleteDataSourceOption = (optionId) => {
    setSelectDataSourceOptions((prev) =>
      prev.filter((item) => item.id !== optionId),
    );
  };
  const handleIntergrationEditOption = (option) => {
    setEditOption(option);
    setIsIntegrationModalOpen(true);
  };
  const handleDataSourceEditOption = (option) => {
    setEditOption(option);
    setIsDataSourceModalOpen(true);
  };

  const [showQuestionForm, setShowQuestionForm] = useState();
  const [showTestScreen, setShowTestScreen] = useState();
  const [isFormLoading, setIsFormLoading] = useState(false);
  const clearStates = () => {
    setSelectTrigerOptions([]);
    setSelectIntegrationOptions([]);
    setSelectDataSourceOptions([]);
    setTriggerName('');
    setInstruction('');
  };
  const handleNewInstructionSubmission = async () => {
    setIsFormLoading(true);
    try {
      const final_response = await createAndHandleClarificationQuestions(
        instruction,
        triggerName,
        selectTrigerOptions,
        selectIntegrationOptions,
        selectDataSourceOptions,
      );
      if (final_response && final_response.questions) {
        setShowQuestionForm(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
    } finally {
      setIsFormLoading(false);
    }
  };
  const handleShowTestScreen = () => {
    setShowTestScreen(true);
  };

  return (
    <div className="flex items-center justify-center duration-200">
      {!showTestScreen && !showQuestionForm && (
        <div className="bg-2 w-full overflow-auto rounded-lg">
          <div className="flex flex-col gap-8 p-6">
            <div className="flex flex-col gap-1">
              <div className="flex flex-col gap-1">
                <h1 className="text-base font-semibold">
                  Name of the Meeting Co Pilot
                </h1>
              </div>
              <input
                value={meetingName}
                onChange={(e) => setMeetingName(e.target.value)} // Update instruction state
                className="bg-2 br-1 resize-none rounded-lg border bg-transparent px-4 py-2 text-base outline-none"
                placeholder=""
              />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex flex-col gap-1">
                <h1 className="text-base font-semibold">
                  Meeting instructions
                </h1>
              </div>
              <textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)} // Update instruction state
                className="bg-2 br-1 h-52 resize-none rounded-lg border bg-transparent px-4 py-2 text-base outline-none"
                placeholder="Instructions describe your workflow requirements in detail. Include all necessary steps, data processing instructions, and desired outcomes across integrated applications (e.g. Jira, Email, Sheets, Docs). Example: 'For every email received in the inbox, send an automated apology response explaining a 34 hour delay in support. Then, create a Jira task under the 'Support' Project, including the email content and tagging it as 'High Priority'. Notify the team lead in Slack once task is created."
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-1 ">
                  <h1 className="text-base font-semibold">App integrations</h1>
                  <p className="text-sm">
                    Add applications included in the workflow
                  </p>
                </div>
                <button
                  onClick={() => setIsIntegrationModalOpen(true)}
                  className="bg-1 p-2 text-sm transition-all duration-200"
                >
                  <MdAdd />
                </button>
              </div>
              {selectIntegrationOptions.map((item) => (
                <SelectedTriggerItem
                  item={item}
                  handleEditOption={() => handleIntergrationEditOption(item)}
                  handleDeleteOption={handleDeleteIntegrationOption}
                />
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-1 ">
                  <h1 className="text-base font-semibold">Data sources</h1>
                  <p className="text-sm">
                    Add data sources required in the workflow
                  </p>
                </div>
                <button
                  onClick={() => setIsDataSourceModalOpen(true)}
                  className="bg-1 p-2 text-sm transition-all duration-200"
                >
                  <MdAdd />
                </button>
              </div>
              {selectDataSourceOptions.map((item) => (
                <SelectedTriggerItem
                  item={item}
                  handleEditOption={() => handleDataSourceEditOption(item)}
                  handleDeleteOption={handleDeleteDataSourceOption}
                />
              ))}
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex flex-col gap-1">
                <h1 className="text-base font-semibold">Meeting link</h1>
              </div>
              <input
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)} // Update instruction state
                className="bg-2 br-1 resize-none rounded-lg border bg-transparent px-4 py-2 text-base outline-none"
                placeholder=""
              />
            </div>{' '}
            <div className="flex flex-col gap-1">
              <div className="flex flex-col gap-1">
                <h1 className="text-base font-semibold">
                  Timing of the Meeting
                </h1>
              </div>
              <input
                value={meetingTiming}
                type={'datetime-local'}
                onChange={(e) => setMeetingTiming(e.target.value)} // Update instruction state
                className="bg-2 br-1 resize-none rounded-lg border bg-transparent px-4 py-2 text-base outline-none"
                placeholder=""
              />
            </div>
            <div className="flex justify-end">
              <Button
                loading={isFormLoading}
                onClick={handleNewInstructionSubmission}
              >
                Continue
              </Button>
            </div>
            {botCreationId && (
              <TriggerModal
                title="Add integration"
                desc=" Select the app you’d like to add as integration."
                isOpen={isIntegrationModalOpen}
                onClose={() => {
                  setIsIntegrationModalOpen(false);
                  setEditOption(null);
                }}
                appsData={integrationData}
                selectedOptions={selectIntegrationOptions}
                setSelectedOptions={setSelectIntegrationOptions}
                editOption={editOption}
                botCreationId={botCreationId}
              />
            )}
            {botCreationId && (
              <TriggerModal
                title="Add data source"
                desc=" Select the app you’d like to add the data source from."
                isOpen={isDataSourceModalOpen}
                onClose={() => {
                  setIsDataSourceModalOpen(false);
                  setEditOption(null);
                }}
                appsData={dataSources}
                selectedOptions={selectDataSourceOptions}
                setSelectedOptions={setSelectDataSourceOptions}
                editOption={editOption}
                botCreationId={botCreationId}
              />
            )}
          </div>
        </div>
      )}
      {showQuestionForm && !showTestScreen && (
        <div className="h-[90%] w-[90%] max-w-3xl overflow-auto rounded-lg bg-zinc-900">
          <div className="flex justify-between gap-3 border-b border-zinc-800 px-3 py-5">
            <div className="flex items-center gap-3 bg-transparent px-3 py-1.5 text-xl font-bold">
              <button
                onClick={() => setShowQuestionForm(false)}
                className="bg-2 rounded-lg p-1.5 transition-all duration-200"
              >
                <FaArrowLeftLong />
              </button>
              Clarification Questions
            </div>
            <div></div>
          </div>
          <div className="flex flex-col gap-8 p-6">
            <QuestionForm
              handleShowTestScreen={handleShowTestScreen}
              closeModal={closeModal}
              setShowQuestionForm={setShowQuestionForm}
              clearStates={clearStates}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const MeetingDetails = () => {
  const [selectedTab, setSelectedTab] = useState('actionable');
  const handleSelectTab = (tab) => {
    setSelectedTab(tab);
  };
  return (
    <div className="relative mt-4 h-full w-full rounded-lg py-3 pb-10">
      <div className="bg-3 br-1 absolute left-1/2 z-50 mt-3 flex -translate-x-1/2 items-center justify-center gap-1 rounded-full border p-1">
        <button
          onClick={() => handleSelectTab('actionable')}
          class={`flex min-h-6 items-center gap-1 rounded-full border-0 px-3.5 py-1 text-xs transition-all duration-200 disabled:cursor-not-allowed ${
            selectedTab === 'actionable'
              ? 'bg-1 hover:text-s-text text-p-text font-semibold'
              : 'hover:text-s-text text-p-text bg-transparent font-normal'
          }`}
        >
          <span>Backlogs</span>
        </button>
        <button
          onClick={() => handleSelectTab('notes')}
          class={`flex min-h-6 items-center gap-1 rounded-full border-0 px-3.5 py-1 text-xs transition-all duration-200 disabled:cursor-not-allowed ${
            selectedTab === 'notes'
              ? 'bg-1 hover:text-s-text text-p-text font-semibold'
              : 'hover:text-s-text bg-transparent font-normal'
          }`}
        >
          <span>Kanban</span>
        </button>
      </div>

      {selectedTab === 'actionable' && <Backlog />}
      {selectedTab === 'notes' && <KanbanBoard />}
    </div>
  );
};

const Meeting = () => {
  const location = useLocation();
  const { pathname } = location;
  const { userInfo } = DataState();
  const [openCreate, setOpenCreate] = useState(false);
  const openCreateModal = () => {
    setOpenCreate(true);
  };
  const closeCreateModal = () => {
    setOpenCreate(false);
  };
  const [input, setInput] = useState('');

  return (
    <BaseLayout createName="New Meeting" handleCreate={openCreateModal}>
      <div className="mx-auto  flex  h-full w-full flex-col gap-3 px-1">
        {false ? (
          <>
            {userInfo && (
              <div className="flex items-center gap-2">
                <img
                  alt="profile"
                  src={userInfo.picture}
                  className="h-8 w-8 rounded-full object-cover"
                />
                <span className="text-sm">{userInfo.name}</span>
              </div>
            )}
            <div className="flex flex-1 flex-col items-center justify-center gap-10">
              <div className="w-full">
                <h1 className="text-5xl font-bold leading-snug">
                  <span className="gradient-text mr-2 font-bold">Namaste!</span>
                  I'm Supermanager AI
                  <br />
                  Let’s Me Automate your Actionalble items from Meeting
                </h1>
                <h2 className="text-3xl text-zinc-500">
                  Create your Meeting Co Pilot{' '}
                </h2>
              </div>
            </div>
          </>
        ) : (
          <MeetingDetails />
        )}
        {pathname !== '/meeting' && (
          <div className="relative">
            <button
              onClick={openCreateModal}
              className="absolute -top-11 left-1/2 flex -translate-x-1/2 transform items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-1 transition-all duration-200 hover:bg-opacity-70"
            >
              + Meeting Co Pilot
            </button>
            <AutoResizableTextarea
              setInput={setInput}
              input={input}
              handleSend={() => {}}
            />
          </div>
        )}
      </div>
      <Modal
        title="Create Meeting"
        isOpen={openCreate}
        onClose={closeCreateModal}
      >
        <CreateMeetingForm />
      </Modal>
    </BaseLayout>
  );
};

export default Meeting;
